/**
 * @version 1.0alpha
 * @author Kelnik Studios {http://kelnik.ru}
 * @link https://kelnik.gitbooks.io/kelnik-documentation/content/front-end/components/popup.html documentation
 */

/**
 * DEPENDENCIES
 */
import actionContentTemplate from './contentTemplates/action-content-popup.twig';
import axios from 'axios';
import Observer from 'common/scripts/observer';
import progressContentTemplate from './contentTemplates/progress-content-popup.twig';
import template from './templates/popup.twig';
import templateMarginless from './templates/popup-marginless.twig';
import Utils from 'common/scripts/utils';

const templates = {
    classic   : template,
    marginless: templateMarginless
};

const contents = {
    action  : actionContentTemplate,
    progress: progressContentTemplate
};

const errors = {
    emptyTemplate       : `Отсутствует шаблон для попапа. Создай twig и добавь его в templates`,
    emptyContentTemplate: `Дружище - ты получаешь контент аяксом. 
        Создай twig шаблон контента и укажи его в настройках и объекте contents`
};

const observer = new Observer();

class Popup {
    constructor(options) {
        this.options = options;
        this.openButtons = options.openButtons;
        this.openLoadPage = options.openLoadPage !== false;
        this.body = document.body;
        this.closeButtonAriaLabel = options.closeButtonAriaLabel || 'Закрыть всплывающее окно';
        this.stateClass = 'is-open';
        this.animateClass = 'is-animate';
        // ID кнопок открытия текущего попапа
        this.openButtons.ids = [];
        // Нужно ли удалять попап
        this.needRemovePopup = options.remove || false;
    }

    init() {
        this._bindEvents();
        this._setTemplate();
        this._parseUrl();
        this._subscribes();
    }

    _setTemplate() {
        this.template = templates[this.options.template] || console.error(errors.emptyContentTemplate);
    }

    _setContentTemplate() {
        this.templateContent = contents[this.options.templateContent] || console.error(errors.emptyTemplate);
    }

    /**
     * Вешаем слушателей событий
     */
    _bindEvents() {
        this.openButtons.forEach((item) => {
            this.openButtons.ids.push(item.dataset.popupId);

            item.addEventListener('click', (event) => {
                event.preventDefault();

                this.target = event.currentTarget;
                this.id = this.target.dataset.popupId;
                this.planoplanUid = this.target.dataset.planoplan;

                this._makeOpen();
            });
        });

        document.addEventListener('keyup', (element) => {
            this._closeOnPressButton(element);
        });
    }

    /**
     * Метод содержит в себе колбэки событий других модулей.
     */
    _subscribes() {
        observer.subscribe('sendForm', (popupInstance) => {
            // если закрываемый попап, равен текущему экземпляру
            if (popupInstance === this) {
                this._close();
            }
        });
    }

    /**
     * Байнд событий после создания popup.
     */
    _bindEventsAfterOpen() {
        this.closeButton.addEventListener('click', this._close.bind(this));
        this.popup.addEventListener('click', (element) => {
            this._closeOnClickOverlay(element);
        });
    }

    /**
     * Запуск попапа при клике на кнопку.
     */
    _makeOpen() {
        this.openPopup = this.body.querySelector(`[data-popup="${this.id}"]`);

        if (this.openPopup) {
            this.popup = this.openPopup;
            this._show();
        } else {
            this._contentTypeCheck();
        }

        if (this.planoplanUid) {
            this._planoplanInit();
        }

        observer.publish('createPopup', this.popup, this);
    }

    /**
     * Проверка типа попапа - статика/ajax.
     */
    _contentTypeCheck() {
        const dataAttributeHref = this.target.dataset.href;
        const dataAttributeAjax = this.target.dataset.ajax;
        const dataAttributeJson = this.target.dataset.json;

        if (dataAttributeHref) {
            const contentId = document.getElementById(dataAttributeHref);

            if (!contentId) {
                console.error(`Создай контентный блок попапа с id "${dataAttributeHref}"`);

                return;
            }

            const staticContent = contentId.innerHTML;

            this._outputOnDisplay(staticContent);

            return;
        }

        if (dataAttributeAjax) {
            this._outputOnDisplay();
            this._setContentTemplate();
            this._collectData();
            this._send();

            return;
        }

        if (dataAttributeJson) {
            const response = JSON.parse(atob(dataAttributeJson));
            const content = response.data.content;

            this._outputOnDisplay();
            this._setContentTemplate();
            this._insetContent(this.content, this.templateContent(content));
        }
    }

    /**
     * Вывод попапа на экран.
     * @param {String} content - контент из статики или из json.
     */
    _outputOnDisplay(content) {
        this._setData(content);
        this._insertTemplate();
        this._getElements();
        this._show();
    }

    /**
     * Подготовка данных для шаблона
     * @param {String} content - контент из статики или из json.
     */
    _setData(content) {
        this.data = {
            content,
            id             : this.id,
            buttonAriaLabel: this.closeButtonAriaLabel
        };
    }

    /**
     * Вставка шаблона.
     */
    _insertTemplate() {
        this.body.insertAdjacentHTML('afterbegin', this.template(this.data));
    }

    /**
     * Получаем попап и его элементы, вешаем события.
     */
    _getElements() {
        this.popup = document.querySelector('.popup');
        this.content = document.querySelector('.popup__content');
        this.closeButton = this.popup.querySelector('.popup__close');
        this.overlay = this.popup.querySelector('.popup__overlay');
        this.preloader = this.popup.querySelector('.popup__preloader');
        this._bindEventsAfterOpen();
    }

    /**
     * Вставка контента
     * @param {String} popup - элемент, куда вставляется контент. (не сам попап, а его контентная часть!)
     * @param {String} content - вставляемый контент.
     */
    _insetContent(popup, content) {
        Utils.clearHtml(popup);
        Utils.insetContent(popup, content);

        observer.publish('insetContentPopup', popup, this);
    }

    _collectData() {
        this.formData = new FormData();
        this.formData.append('id', this.target.dataset.id);
    }

    /**
     * Метод отправки ajax запроса
     * @param {Object} sentData - отправляемые данные.
     * @return {Boolean} - false - если нет интернета, запрос прерывается.
     * @return {Boolean} - true - успешное выполнение запроса.
     */
    _send() {
        this._showPreloader();

        if (!this._checkInternet()) {
            this._resending();

            return false;
        }

        axios.post(this.target.dataset.ajax, this.formData)
            .then((response) => {
                const data = response.data.data;

                if (data) {
                    this._insetContent(this.content, this.templateContent(data.content));
                } else {
                    this._errorHandler('data');
                    console.error(response.request.errors);
                }

                this._hidePreloader();
            })
            .catch((error) => {
                this._hidePreloader();
                this._errorHandler('server');
                console.error(`server error ${error}`);
            });

        return true;
    }

    /**
     * Метод закрытия попапа по клику на оверлей.
     * @param {Object} element - элемент по которому произошло событие. Cм. метод bindEventsAfterOpen
     */
    _closeOnClickOverlay(element) {
        if (element.target === this.overlay) {
            this._close();
        }
    }

    /**
     * Метод закрытия попапа по нажатию на ESC
     * @param {Object} element - элемент по которому произошло событие. Cм. метод bindEvents
     */
    _closeOnPressButton(element) {
        const keyCodeESC = 27;

        if (element.keyCode === keyCodeESC) {
            this._close();
        }
    }

    /**
     * Метод открывает попап
     */
    _show() {
        this.popup.classList.add(this.stateClass);
        this._setUrl();

        setTimeout(() => {
            this.popup.classList.add(this.animateClass);
        }, 100);

        Utils.bodyFixed(this.popup);
        observer.publish('showPopup', this);
    }

    /**
     * Метод закрывает попап
     */
    _close() {
        this.popup.classList.remove(this.animateClass);
        this._clearUrl();

        if (this.needRemovePopup) {
            this._remove();
        }

        setTimeout(() => {
            this.popup.classList.remove(this.stateClass);
            Utils.bodyStatic();
            observer.publish('closePopup');
        }, 300);
    }

    /**
     * Удаление попапа со страницы.
     */
    _remove() {
        Utils.removeElement(this.popup);
        Utils.bodyStatic();
    }

    /**
     * Метод обработки ошибок.
     * @param {String} typeError - тип ошибки.
     */
    _errorHandler(typeError) {
        const errorList = [{
            type: 'internet',
            html: '<h1>Вы не подключены к интернету. Повторите запрос позднее</h1>'
        }, {
            type: 'server',
            html: '<h1>На сервере произошла ошибка. Повторите запрос позднее</h1>'
        }, {
            type: 'data',
            html: '<h1>На сервере произошла ошибка. Повторите запрос позднее</h1>'
        }];

        errorList.forEach((item) => {
            if (typeError === item.type) {
                this._insetContent(this.content, item.html);
            }
        });
    }

    /**
     * Проверка на наличие интернета.
     * @return {boolean} true - интернет есть.
     * @return {boolean} false - интернетa нет.
     */
    _checkInternet() {
        clearInterval(this.setInterval);
        if (!Utils.checkInternetConnection()) {
            this._errorHandler('internet');

            return false;
        }

        return true;
    }

    /**
     * Повторный запрос на сервер.
     * @param {object} sentData - отрправляемые данные.
     *
     */
    _resending() {
        const interval = 5000;

        this.setInterval = setInterval(() => {
            this._send();
        }, interval);
    }

    /**
     * Метод показывает прелоадер
     */
    _showPreloader() {
        Utils.show(this.preloader);
    }

    /**
     * Метод скрывает прелоадер
     */
    _hidePreloader() {
        if (this.preloader) {
            Utils.hide(this.preloader);
        }
    }

    /**
     * Метод устанавливает hash в url
     */
    _setUrl() {
        if (!this.openLoadPage) {
            return;
        }

        const hash = this.id;

        window.history.pushState(null, '/', hash);
    }

    /**
     * Метод удаляет hash из url
     */
    _clearUrl() {
        if (!this.openLoadPage) {
            return;
        }

        if (window.history.pushState) {
            window.history.pushState('', '/', window.location.pathname);
        } else {
            window.location.hash = '';
        }
    }

    /**
     * Метод проверяет наличие хэша в url, и открывает соотвествующий попап.
     */
    _parseUrl() {
        const hash = window.location.hash;
        const isPlanoplan = hash.split('-')[0] === '#planoplan';

        if (!this.openLoadPage && hash) {
            return;
        }


        this.target = this.body.querySelector(`[data-popup-id="${hash}"]`);
        this.id = hash;

        if (isPlanoplan) {
            this.planoplanUid = hash.split('-')[1];
        }

        this.openButtons.ids.forEach((item) => {
            if (item === this.id) {
                this._makeOpen();
            }
        });
    }

    /**
     * Инициализация планоплана
     * @private
     */
    _planoplanInit() {
        window.Planoplan.init({
            width          : '100%',
            height         : '100%',
            uid            : this.planoplanUid,
            el             : 'planoplan',
            primaryColor   : '#ee7623',
            secondaryColor : '#F4F7F7',
            textColor      : '#000000',
            backgroundColor: '#FFFFFF'
        });
    }
}

export default Popup;
